import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { validateEmail } from '../../../utils/emailValidation';

const DriverFormModal = ({ driver, onClose, onSave, yards = [], duplicateError = null, onClearDuplicateError }) => {
  const [formData, setFormData] = useState({
    driverId: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    yardLocation: '',
    isActive: true
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (driver) {
      const nameParts = driver?.name?.split(' ') || [];
      const firstName = nameParts?.[0] || '';
      const lastName = nameParts?.slice(1)?.join(' ') || '';
      setFormData({
        driverId: driver?.driverId,
        firstName: firstName,
        lastName: lastName,
        phone: driver?.phone,
        email: driver?.email || '',
        yardLocation: driver?.yardLocationId || '',
        isActive: driver?.isActive
      });
    }
  }, [driver]);

  const yardLocationOptions = yards?.length > 0
    ? yards?.map(y => ({ value: String(y?.id), label: y?.name }))
    : [];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.firstName?.trim()) newErrors.firstName = 'First name is required';
    if (!formData?.lastName?.trim()) newErrors.lastName = 'Last name is required';
    
    const phoneRegex = /^\+1\d{10}$/;
    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!phoneRegex?.test(formData?.phone)) {
      newErrors.phone = 'Phone must be in E.164 format (+1XXXXXXXXXX)';
    }

    const emailErr = validateEmail(formData?.email);
    if (emailErr) newErrors.email = emailErr;

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const formatPhoneNumber = (value) => {
    const digits = value?.replace(/\D/g, '');
    if (digits?.length <= 11) {
      if (digits?.startsWith('1')) {
        return `+${digits}`;
      } else if (digits?.length === 10) {
        return `+1${digits}`;
      }
    }
    return value;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e?.target?.value);
    setFormData(prev => ({ ...prev, phone: formatted }));
    onClearDuplicateError?.();
  };

  const handleEmailChange = (e) => {
    const val = e?.target?.value;
    setFormData(prev => ({ ...prev, email: val }));
    onClearDuplicateError?.();
    const err = validateEmail(val);
    setErrors(prev => ({ ...prev, email: err || undefined }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    setTimeout(() => {
      onSave({
        ...formData,
        name: `${formData?.firstName} ${formData?.lastName}`
      });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevation-3 w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
              <Icon name={driver ? "Edit2" : "UserPlus"} size={20} color="var(--color-primary)" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {driver ? 'Edit Driver' : 'Add New Driver'}
              </h2>
              <p className="text-xs text-muted-foreground">
                {driver ? 'Update driver information' : 'Enter driver details'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-hover"
          >
            <Icon name="X" size={20} strokeWidth={2} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="First Name"
                type="text"
                placeholder="John"
                value={formData?.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e?.target?.value }))}
                error={errors?.firstName}
                required
              />
              <Input
                label="Last Name"
                type="text"
                placeholder="Doe"
                value={formData?.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e?.target?.value }))}
                error={errors?.lastName}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Phone Number"
                type="tel"
                placeholder="+15551234567"
                value={formData?.phone}
                onChange={handlePhoneChange}
                description="E.164 format: +1XXXXXXXXXX"
                error={errors?.phone}
                required
              />
              <Input
                label="Email Address"
                type="text"
                placeholder="john.doe@company.com"
                value={formData?.email}
                onChange={handleEmailChange}
                error={errors?.email}
              />
            </div>

            {duplicateError && (
              <div className="flex items-start space-x-2 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="mt-0.5 h-4 w-4 shrink-0 text-destructive" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                <p className="text-sm font-medium text-destructive">{duplicateError}</p>
              </div>
            )}

            <div className="border-t border-border pt-4">
              <h3 className="text-sm font-semibold text-foreground mb-4">Assignment Details</h3>
              <Select
                label="Yard Location"
                options={yardLocationOptions}
                value={formData?.yardLocation}
                onChange={(value) => setFormData(prev => ({ ...prev, yardLocation: value }))}
                error={errors?.yardLocation}
                placeholder={yardLocationOptions?.length === 0 ? 'No yards available' : 'Select yard...'}
              />
            </div>

            <div className="border-t border-border pt-4">
              <Checkbox
                label="Active Driver"
                description="Driver is currently active and available for assignments"
                checked={formData?.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e?.target?.checked }))}
              />
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-border bg-muted/30">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            loading={isSubmitting}
            iconName="Save"
          >
            {driver ? 'Update Driver' : 'Add Driver'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DriverFormModal;